import React, { createContext, useContext, ReactNode, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { UserRole, AdminPermissions } from '../types';
import FirestoreService from '../services/firestoreService';

interface AdminContextType {
  isAdmin: boolean;
  isSuperAdmin: boolean;
  canManageUsers: boolean;
  canManagePrograms: boolean;
  canViewAnalytics: boolean;
  canBroadcastNotifications: boolean;
  canAccessAudit: boolean;
  canManageRoles: boolean;
  userRole: UserRole | undefined;
  permissions: AdminPermissions | undefined;
  assignAdminRole: (userId: string, role: UserRole) => Promise<void>;
  removeAdminRole: (userId: string) => Promise<void>;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const AdminProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [permissions, setPermissions] = React.useState<AdminPermissions>(() => 
    getDefaultPermissions(user?.role)
  );

  // Default permissions based on role
  function getDefaultPermissions(role?: UserRole): AdminPermissions {
    switch (role) {
      case 'superadmin':
        return {
          manageUsers: true,
          managePrograms: true,
          viewAnalytics: true,
          broadcastNotifications: true,
          auditAccess: true,
          roleManagement: true,
        };
      case 'admin':
        return {
          manageUsers: true,
          managePrograms: true,
          viewAnalytics: true,
          broadcastNotifications: true,
          auditAccess: true,
          roleManagement: false,
        };
      case 'moderator':
        return {
          manageUsers: true,
          managePrograms: false,
          viewAnalytics: true,
          broadcastNotifications: false,
          auditAccess: false,
          roleManagement: false,
        };
      default:
        return {
          manageUsers: false,
          managePrograms: false,
          viewAnalytics: false,
          broadcastNotifications: false,
          auditAccess: false,
          roleManagement: false,
        };
    }
  }

  // Update permissions whenever user role changes (fixes admin button not appearing immediately)
  React.useEffect(() => {
    const newPermissions = user?.permissions || getDefaultPermissions(user?.role);
    setPermissions(newPermissions);
    console.log('🔐 AdminContext: Updated permissions for role:', user?.role, 'isAdmin:', newPermissions.manageUsers);
  }, [user?.role, user?.permissions]);

  const assignAdminRole = useCallback(
    async (userId: string, role: UserRole) => {
      // Only superadmin or current admin can assign roles
      if (user?.role !== 'superadmin') {
        throw new Error('Only superadmin can assign admin roles');
      }

      try {
        const permissions = getDefaultPermissions(role);
        await FirestoreService.updateUserRole(userId, role, permissions);
        
        // Log the action
        await FirestoreService.logAdminAction(user.id, 'ASSIGN_ROLE', userId, 'user', {
          newRole: role,
          permissions,
        });

        console.log(`✅ Admin role assigned: ${userId} -> ${role}`);
      } catch (error) {
        console.error('❌ Error assigning admin role:', error);
        throw error;
      }
    },
    [user]
  );

  const removeAdminRole = useCallback(
    async (userId: string) => {
      if (user?.role !== 'superadmin') {
        throw new Error('Only superadmin can remove admin roles');
      }

      try {
        await FirestoreService.updateUserRole(userId, 'user', getDefaultPermissions('user'));
        
        // Log the action
        await FirestoreService.logAdminAction(user.id, 'REMOVE_ROLE', userId, 'user', {
          previousRole: 'admin',
        });

        console.log(`✅ Admin role removed: ${userId}`);
      } catch (error) {
        console.error('❌ Error removing admin role:', error);
        throw error;
      }
    },
    [user]
  );

  const value: AdminContextType = {
    isAdmin: user?.role === 'admin' || user?.role === 'superadmin',
    isSuperAdmin: user?.role === 'superadmin',
    canManageUsers: permissions?.manageUsers || false,
    canManagePrograms: permissions?.managePrograms || false,
    canViewAnalytics: permissions?.viewAnalytics || false,
    canBroadcastNotifications: permissions?.broadcastNotifications || false,
    canAccessAudit: permissions?.auditAccess || false,
    canManageRoles: permissions?.roleManagement || false,
    userRole: user?.role,
    permissions,
    assignAdminRole,
    removeAdminRole,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = (): AdminContextType => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};
